import type { PortableTextComponents } from "@portabletext/react";

import { cn } from "../../utils/classnames";
import {
  ICONS,
  linkIconVariants,
  linkVariants,
  resolveLinkSpacingClasses,
} from "../../utils/rich-text-variants";
import { Link } from "../Link";
import { TrackableLink } from "../TrackableLink";

type CreateLinkMarkOptions = {
  onLinkClick?: (href: string) => void;
};

function createLinkMark({
  onLinkClick,
}: CreateLinkMarkOptions): NonNullable<
  PortableTextComponents["marks"]
>["link"] {
  const RichTextLinkMark: NonNullable<PortableTextComponents["marks"]>["link"] =
    ({ children, value }) => {
      if (!value?.href) {
        return <>{children}</>;
      }

      const variant = value.variant ?? "plain";
      const verticalSpacing = value.verticalSpacing;
      const topSpacing = value.topSpacing;
      const openInNewTab = value.openInNewTab;
      const icon = value.icon;
      const IconComponent = ICONS[icon as keyof typeof ICONS];
      const spacingClasses = resolveLinkSpacingClasses(
        verticalSpacing,
        topSpacing,
      );
      const shouldInheritTypography = variant === "plain";
      const linkClassName = cn(
        linkVariants({
          variant,
          withIcon: !!IconComponent,
        }),
        spacingClasses,
        shouldInheritTypography &&
          "!text-[length:inherit] !font-[inherit] !leading-[inherit] text-inherit",
      );

      const linkContent = (
        <>
          {IconComponent ? (
            <IconComponent
              className={linkIconVariants({ linkVariant: variant })}
              data-testid="link-icon"
            />
          ) : null}
          {children}
        </>
      );

      if (onLinkClick) {
        return (
          <TrackableLink
            className={linkClassName}
            href={value.href}
            onLinkClick={onLinkClick}
            rel={openInNewTab ? "noopener noreferrer" : undefined}
            target={openInNewTab ? "_blank" : undefined}
            variant={variant}
          >
            {linkContent}
          </TrackableLink>
        );
      }

      return (
        <Link
          className={linkClassName}
          href={value.href}
          rel={openInNewTab ? "noopener noreferrer" : undefined}
          target={openInNewTab ? "_blank" : undefined}
          variant={variant}
        >
          {linkContent}
        </Link>
      );
    };

  RichTextLinkMark.displayName = "RichTextLinkMark";

  return RichTextLinkMark;
}

export { createLinkMark };
